// src/pages/Calendar.jsx
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function toDateKey(d) { return d.toISOString().slice(0, 10); }
function isSameDay(a, b) { return toDateKey(a) === toDateKey(b); }

export default function Calendar() {
  const [cursor, setCursor] = useState(() => new Date());
  const [appointments, setAppointments] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const from = startOfMonth(cursor);
      from.setDate(from.getDate() - 7); // small buffer either side so the leading/trailing grid days have data too
      const to = endOfMonth(cursor);
      to.setDate(to.getDate() + 7);
      const body = await api(`/appointments?from=${from.toISOString()}&to=${to.toISOString()}`);
      setAppointments(body.appointments);
    } catch (err) {
      setError(err.message);
    }
  }, [cursor]);

  useEffect(() => { load(); }, [load]);

  const firstDay = startOfMonth(cursor);
  const lastDay = endOfMonth(cursor);
  const leadingBlanks = firstDay.getDay(); // 0 = Sunday
  const daysInMonth = lastDay.getDate();
  const cells = [
    ...Array.from({ length: leadingBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1)),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const apptsFor = (day) => day ? appointments.filter((a) => isSameDay(new Date(a.scheduled_at), day) && a.status !== 'cancelled') : [];
  const selectedAppts = apptsFor(selectedDay).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-charcoal">Appointments</h1>
        <button onClick={() => setShowForm(true)} className="rounded-md bg-ink text-white text-sm font-medium px-4 py-2 hover:bg-ink-700">+ Book appointment</button>
      </div>
      {error && <p className="mt-3 text-sm text-p1">{error}</p>}

      <div className="mt-5 grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="text-sm text-ink/50 hover:text-ink px-2">←</button>
            <div className="text-sm font-medium">{cursor.toLocaleDateString([], { month: 'long', year: 'numeric' })}</div>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="text-sm text-ink/50 hover:text-ink px-2">→</button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wide text-ink/40 mb-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => {
              const dayAppts = apptsFor(day);
              const isSelected = day && isSameDay(day, selectedDay);
              const isToday = day && isSameDay(day, new Date());
              return (
                <button
                  key={i}
                  disabled={!day}
                  onClick={() => day && setSelectedDay(day)}
                  className={`h-16 rounded-md border text-left p-1.5 transition-colors ${
                    !day ? 'border-transparent' :
                    isSelected ? 'border-sage bg-sage-100' :
                    isToday ? 'border-accent/40' : 'border-ink/10 hover:bg-paper'
                  }`}
                >
                  {day && (
                    <>
                      <div className={`text-xs ${isToday ? 'font-bold text-accent' : 'text-ink/70'}`}>{day.getDate()}</div>
                      {dayAppts.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-0.5">
                          {dayAppts.slice(0, 3).map((a) => <span key={a.id} className="h-1.5 w-1.5 rounded-full bg-accent" />)}
                        </div>
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ink/50 mb-2">
            {selectedDay.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
          </h2>
          <div className="space-y-2">
            {selectedAppts.length === 0 && <p className="text-xs text-ink/40">No appointments this day.</p>}
            {selectedAppts.map((a) => (
              <div key={a.id} className="rounded-lg border border-ink/10 bg-white p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-ink/50">
                    {new Date(a.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-ink/40">{a.mode}</span>
                </div>
                <div className="text-sm font-medium mt-1">{a.title}</div>
                <div className="text-xs text-ink/50 mt-0.5">{a.staff_name}</div>
                {a.case_ref && (
                  <Link to={`/cases/${a.case_id}`} className="text-xs text-accent hover:underline mt-1 inline-block font-mono">{a.case_ref}</Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showForm && (
        <BookAppointmentModal
          defaultDate={selectedDay}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function BookAppointmentModal({ defaultDate, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', mode: 'phone', location: '',
    date: toDateKey(defaultDate), time: '09:00', durationMinutes: 30, notes: '',
  });
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    try {
      await api('/appointments', {
        method: 'POST',
        body: JSON.stringify({
          title: form.title, mode: form.mode, location: form.location || undefined,
          scheduledAt: `${form.date} ${form.time}:00`, durationMinutes: Number(form.durationMinutes),
          notes: form.notes || undefined,
        }),
      });
      onSaved();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-40">
      <form onSubmit={submit} className="bg-white rounded-xl p-5 max-w-sm w-full space-y-3">
        <h2 className="font-semibold text-charcoal">Book appointment</h2>
        <input required placeholder="Title / purpose" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-md border border-ink/20 px-3 py-2 text-sm" />
          <input required type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-md border border-ink/20 px-3 py-2 text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="rounded-md border border-ink/20 px-3 py-2 text-sm">
            <option value="phone">Phone</option>
            <option value="video">Video</option>
            <option value="in_person">In person</option>
          </select>
          <input type="number" min={15} step={15} value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} className="rounded-md border border-ink/20 px-3 py-2 text-sm" placeholder="Minutes" />
        </div>
        {form.mode === 'in_person' && (
          <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
        )}
        <textarea placeholder="Notes (optional)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm" />
        {error && <p className="text-xs text-p1">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 text-sm text-ink/60 py-2">Cancel</button>
          <button type="submit" className="flex-1 rounded-md bg-ink text-white text-sm font-medium py-2">Book</button>
        </div>
      </form>
    </div>
  );
}
